import React from 'react'
import { DateRangePicker as RsDateRangePicker } from 'rsuite'
import {
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  endOfMonth,
  startOfYear
} from 'date-fns'
import './dateRangePicker.scss'

/** A [from, to] pair of dates, matching RSuite's DateRangePicker value. */
export type DateRangeValue = [Date, Date]

type RsProps = React.ComponentProps<typeof RsDateRangePicker>

export interface DateRangePickerProps {
  /** Optional label rendered above the control (form-style). Omit for inline/toolbar use. */
  label?: string
  value?: DateRangeValue | null
  onChange: (range: DateRangeValue | null) => void
  placeholder?: string
  disabled?: boolean
  /** Show the clear (×) button. Default true. */
  cleanable?: boolean
  /** Stretch to the full width of the parent. Default true. */
  block?: boolean
  /** Show the quick-range presets (Today, Last 7 Days, …). Default true. */
  showPresets?: boolean
  /** Disable selection of any date after today. Default false. */
  disableFuture?: boolean
  size?: RsProps['size']
  placement?: RsProps['placement']
  className?: string
  name?: string
}

// Quick ranges are computed on render so "Today" etc. stay relative to now.
const buildPresets = (): NonNullable<RsProps['ranges']> => {
  const today = new Date()
  return [
    { label: 'Today', value: [startOfDay(today), endOfDay(today)] },
    {
      label: 'Last 7 Days',
      value: [startOfDay(subDays(today, 6)), endOfDay(today)]
    },
    {
      label: 'Last 30 Days',
      value: [startOfDay(subDays(today, 29)), endOfDay(today)]
    },
    {
      label: 'This Month',
      value: [startOfMonth(today), endOfMonth(today)]
    },
    {
      label: 'This Year',
      value: [startOfYear(today), endOfDay(today)]
    }
  ]
}

/**
 * Reusable date-range picker that wraps RSuite's DateRangePicker with the
 * project's date format, optional quick-range presets and an optional label.
 */
function DateRangePicker({
  label,
  value,
  onChange,
  placeholder = 'Select date range',
  disabled,
  cleanable = true,
  block = true,
  showPresets = true,
  disableFuture = false,
  size,
  placement,
  className,
  name
}: Readonly<DateRangePickerProps>): React.ReactElement {
  const shouldDisableDate = disableFuture
    ? (date?: Date) => (date ? date > endOfDay(new Date()) : false)
    : undefined

  const pickerClassName = ['date-range-picker', className]
    .filter(Boolean)
    .join(' ')

  const picker = (
    <RsDateRangePicker
      id={name}
      value={value ?? null}
      onChange={(range) => onChange(range)}
      format="dd/MM/yyyy"
      character=" – "
      placeholder={placeholder}
      placement={placement}
      size={size}
      block={block}
      cleanable={cleanable}
      disabled={disabled}
      ranges={showPresets ? buildPresets() : []}
      shouldDisableDate={shouldDisableDate}
      className={pickerClassName}
    />
  )

  if (!label) return picker

  return (
    <div className="formInputWrapper date-range-picker__wrapper">
      <p className="inputLabel">
        <label htmlFor={name}>{label}</label>
      </p>
      {picker}
    </div>
  )
}

export default DateRangePicker
